/**
 * postMessage contract between `@0gate/gate-js` and the iframe.
 *
 * This is the SOURCE OF TRUTH for the host ↔ iframe message names.
 * The iframe-side handler in `0Gate_Widget/Frontend/src/iframe-app/src/main.ts`
 * speaks the same constants. Don't rename one side without updating the other.
 *
 * Protocol direction:
 *   host  → iframe   INIT_CONFIG, SET_THEME, UPDATE_CONFIG
 *   iframe → host    WIDGET_READY, PAYMENT_SUCCESS, PAYMENT_ERROR, PAYMENT_CLOSE
 */

export const MessageType = {
   /** host → iframe — first config push after WIDGET_READY. */
   INIT_CONFIG: 'INIT_CONFIG',
   /** host → iframe — at any point, swap theme. */
   SET_THEME: 'SET_THEME',
   /** host → iframe — partial config update (amount, currency, etc.). */
   UPDATE_CONFIG: 'UPDATE_CONFIG',

   /** iframe → host — emitted on app mount; host responds with INIT_CONFIG. */
   WIDGET_READY: 'WIDGET_READY',
   /** iframe → host — terminal happy path. Data shape: { txId, sessionId? }. */
   PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
   /** iframe → host — terminal sad path. Data shape: { code, message }. */
   PAYMENT_ERROR: 'PAYMENT_ERROR',
   /** iframe → host — user dismissed without completing. */
   PAYMENT_CLOSE: 'PAYMENT_CLOSE',
} as const;

export type MessageTypeValue = (typeof MessageType)[keyof typeof MessageType];

// ---- Host → iframe payloads -----------------------------------------

export interface InitConfigPayload {
   /** Always a pk_* — sk_* is rejected at GateRamp construction. */
   publishableKey: string;
   /** Optional. Required for session-bound flows. */
   clientSecret?: string;
   /** Optional. Echoed by the iframe; useful for the UI to show context. */
   amount?: string;
   currency?: string;
   /** Optional partner constraint forwarded to the iframe. */
   targetToken?: string;
   targetNetwork?: string;
   /** Where the iframe redirects after a successful flow. */
   returnUrl?: string;
   /**
    * Upgrade 14 follow-up — when `false`, the iframe-app renders its
    * neutral "service unavailable" overlay instead of the normal
    * checkout UI. Omitted / `true` = proceed as normal.
    */
   available?: boolean;
   /** Stable reason string: `quota_exhausted`, `partner_suspended`, etc. */
   unavailable_reason?: string | null;
   /**
    * Upgrade 15 — kit-block flow lock. When set, the iframe hides the
    * buy/sell/swap tab strip and forces the user into the chosen flow.
    * Sourced from the GateSession created by the partner's server.
    */
   flow?: GateFlow | null;
   /**
    * Upgrade 16 — `true` when the partner submitted (and we accepted)
    * a pre-verified KYC package on session create. The iframe can use
    * this to skip its own KYC step where applicable. Never PII.
    */
   kyc_pre_verified?: boolean;
   /** Upgrade 16 — pre-filled destination wallet from the session. */
   walletAddress?: string;
   /** Upgrade 16 — opaque partner-side identifier (order id, user id, etc.). */
   userReference?: string;
}

/** Kit-block flow discriminator. Mirrors backend `GateSession.flow`. */
export type GateFlow = 'on_ramp' | 'off_ramp' | 'swap';

export interface SetThemePayload {
   theme: 'light' | 'dark';
}

export interface UpdateConfigPayload {
   amount?: string;
   currency?: string;
   targetToken?: string;
   targetNetwork?: string;
   /** Reserved. */
   metadata?: Record<string, unknown>;
}

// ---- Iframe → host payloads -----------------------------------------

export interface WidgetReadyData {
   /** Reserved for the iframe to advertise capability flags. */
   capabilities?: string[];
}

export interface PaymentSuccessData {
   /** Internal transaction id. Use to query session status. */
   txId: string;
   /** Set when the flow was session-bound. */
   sessionId?: string;
   /** Optional — populated for off-ramp / completed swaps. */
   amount?: string;
   currency?: string;
}

export interface PaymentErrorData {
   code: string;
   message: string;
   /** Set if the failure happened against a known intent. */
   intentId?: string;
}

export interface PaymentCloseData {
   /**
    * Why the user dismissed the iframe. `'user_close'` = clicked X,
    * `'session_expired'` = session TTL elapsed, etc.
    */
   reason: 'user_close' | 'session_expired' | 'unknown';
}

// ---- Discriminated union for incoming messages ----------------------

export type IframeToHostMessage =
   | { type: typeof MessageType.WIDGET_READY; data?: WidgetReadyData }
   | { type: typeof MessageType.PAYMENT_SUCCESS; data: PaymentSuccessData }
   | { type: typeof MessageType.PAYMENT_ERROR; data: PaymentErrorData }
   | { type: typeof MessageType.PAYMENT_CLOSE; data: PaymentCloseData };

export type HostToIframeMessage =
   | { type: typeof MessageType.INIT_CONFIG; data: InitConfigPayload }
   | { type: typeof MessageType.SET_THEME; data: SetThemePayload }
   | { type: typeof MessageType.UPDATE_CONFIG; data: UpdateConfigPayload };

/**
 * Type-guard. Useful in app code that has a generic message listener
 * and wants to narrow before dispatching.
 */
export function isIframeMessage(value: unknown): value is IframeToHostMessage {
   if (!value || typeof value !== 'object') return false;
   const t = (value as { type?: unknown }).type;
   return (
      t === MessageType.WIDGET_READY ||
      t === MessageType.PAYMENT_SUCCESS ||
      t === MessageType.PAYMENT_ERROR ||
      t === MessageType.PAYMENT_CLOSE
   );
}
